import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, Copy, ExternalLink, Link2, Loader2, QrCode, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { getApiUrl } from '@/lib/api'
import { fetchOnlineBookingShare } from '@/lib/api/publicBooking'
import { cn } from '@/lib/utils'

async function copyText(value) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value)
    return
  }

  const input = document.createElement('textarea')
  input.value = value
  document.body.appendChild(input)
  input.select()
  document.execCommand('copy')
  document.body.removeChild(input)
}

/**
 * @param {{
 *   isModal?: boolean,
 *   onClose?: () => void,
 *   className?: string,
 *   title?: string,
 *   subtitle?: string,
 *   children: import('react').ReactNode,
 * }} props
 */
function SharePanelShell({
  isModal = false,
  onClose,
  className = '',
  title = 'Share online booking',
  subtitle = 'Public links for guests',
  children,
}) {
  return (
    <div
      className={cn(
        isModal ? 'flex flex-col bg-card' : 'rounded-2xl border border-border/60 bg-card p-4 shadow-luxury-sm',
        className,
      )}
    >
      <div
        className={cn(
          'flex shrink-0 items-start gap-3',
          isModal ? 'border-b border-border/60 px-6 py-5' : '',
        )}
      >
        <div
          className={cn(
            'flex shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary',
            isModal ? 'h-12 w-12' : 'h-10 w-10',
          )}
        >
          <Link2 className={isModal ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className={cn('font-semibold text-foreground', isModal ? 'text-lg' : 'text-sm')}>
            {title}
          </p>
          {subtitle ? (
            <p className={cn('mt-0.5 text-muted-foreground', isModal ? 'text-sm' : 'text-xs')}>
              {subtitle}
            </p>
          ) : null}
        </div>
        {isModal && onClose ? (
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      <div
        className={cn(
          isModal ? 'px-6 py-5' : 'mt-3 space-y-3',
        )}
      >
        {children}
      </div>
    </div>
  )
}

function ShareLinkRow({ link, copiedKey, onCopy }) {
  return (
    <div className="group flex items-center gap-2 rounded-xl border border-border/50 bg-muted/20 px-3 py-2.5 transition-shadow hover:shadow-luxury-sm">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
          {link.label}
        </p>
        <p className="mt-0.5 truncate rounded-md bg-background/70 px-2 py-0.5 font-mono text-[11px] text-foreground ring-1 ring-border/30">
          {link.value}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-foreground"
        onClick={() => onCopy(link.key, link.value)}
        aria-label={`Copy ${link.label}`}
      >
        {copiedKey === link.key ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
      </Button>
      <Button type="button" size="sm" variant="ghost" className="h-8 w-8 shrink-0 p-0" asChild>
        <a href={link.value} target="_blank" rel="noopener noreferrer" aria-label={`Open ${link.label}`}>
          <ExternalLink className="h-4 w-4" />
        </a>
      </Button>
    </div>
  )
}

/**
 * Share online booking URLs — available to every signed-in role for the active branch.
 *
 * @param {{
 *   className?: string,
 *   variant?: 'inline' | 'modal',
 *   onClose?: () => void,
 *   initialInfo?: object | null,
 * }} props
 */
export function OnlineBookingSharePanel({
  className = '',
  variant = 'inline',
  onClose,
  initialInfo = null,
}) {
  const { activeBranchId } = useBranch()
  const [info, setInfo] = useState(initialInfo)
  const [copiedKey, setCopiedKey] = useState('')
  const isModal = variant === 'modal'

  useEffect(() => {
    setInfo(initialInfo ?? null)
  }, [initialInfo, activeBranchId])

  useEffect(() => {
    if (!activeBranchId) {
      setInfo(null)
      return
    }

    fetchOnlineBookingShare()
      .then(setInfo)
      .catch(() =>
        setInfo({
          enabled: false,
          message: 'Unable to load booking links. Check your connection or try again.',
          hub_url: null,
        }),
      )
  }, [activeBranchId])

  if (info === null) {
    return (
      <SharePanelShell isModal={isModal} onClose={onClose} className={className}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading booking links…
        </div>
      </SharePanelShell>
    )
  }

  if (!info?.enabled) {
    return (
      <SharePanelShell isModal={isModal} onClose={onClose} className={className}>
        <p className="text-sm text-muted-foreground">
          {info?.message ?? 'Unable to load booking links right now.'}
        </p>
      </SharePanelShell>
    )
  }

  async function handleCopy(key, value) {
    await copyText(value)
    setCopiedKey(key)
    window.setTimeout(() => setCopiedKey(''), 2000)
  }

  const links = [
    { key: 'branch', label: 'This branch', value: info.branch_url },
    { key: 'catalog', label: 'Service catalog', value: info.catalog_url },
    { key: 'hub', label: 'All branches', value: info.hub_url },
  ].filter((link) => Boolean(link.value))

  const qrBaseUrl = info.qr_url
    ?? (info.slug ? `${getApiUrl()}/public/booking/${info.slug}/qr.png` : null)
  const qrImageUrl = qrBaseUrl
    ? `${qrBaseUrl}${qrBaseUrl.includes('?') ? '&' : '?'}v=2`
    : null

  const subtitle = [
    info.branch_name,
    info.manual_approve ? 'requests need approval' : 'auto-confirms',
  ]
    .filter(Boolean)
    .join(' · ')

  const linkList = (
    <div className="space-y-2">
      {links.map((link) => (
        <ShareLinkRow key={link.key} link={link} copiedKey={copiedKey} onCopy={handleCopy} />
      ))}
    </div>
  )

  const qrBlock = qrImageUrl ? (
    <div
      className={cn(
        'rounded-xl border border-border/50 bg-muted/20 text-center',
        isModal ? 'flex flex-col items-center justify-center p-5' : 'p-3',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center gap-2 font-semibold uppercase tracking-wide text-muted-foreground',
          isModal ? 'mb-3 text-xs' : 'mb-1.5 text-[10px]',
        )}
      >
        <QrCode className={isModal ? 'h-4 w-4' : 'h-3.5 w-3.5'} aria-hidden />
        Scan to book
      </div>
      <img
        src={qrImageUrl}
        alt={`QR code for ${info.branch_name} online booking`}
        className={cn(
          'rounded-xl border border-border/40 bg-white object-contain',
          isModal ? 'h-52 w-52 p-3' : 'mx-auto h-32 w-32 p-2',
        )}
      />
      {isModal ? (
        <p className="mt-3 text-xs text-muted-foreground">Print or display at reception</p>
      ) : null}
    </div>
  ) : null

  return (
    <SharePanelShell isModal={isModal} onClose={onClose} className={className} subtitle={subtitle}>
      {isModal ? (
        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_220px] md:items-start">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Share links
            </p>
            {linkList}
          </div>
          {qrBlock}
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-primary/15 bg-primary/5 px-3 py-2">
            <p className="text-[11px] leading-snug text-foreground">
              Bookings appear here and on{' '}
              <Link to="/calendar" className="font-semibold text-primary underline-offset-2 hover:underline">
                Calendar
              </Link>
              .
            </p>
          </div>
          {linkList}
          {qrBlock}
          {info.embed_snippet ? (
            <div className="rounded-xl border border-border/50 bg-muted/20 p-2.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Website embed
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 shrink-0 p-0"
                  onClick={() => handleCopy('embed', info.embed_snippet)}
                >
                  {copiedKey === 'embed' ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
              <pre className="mt-1.5 max-h-16 overflow-auto rounded-lg bg-background/80 p-2 text-[10px] leading-relaxed text-foreground">
                {info.embed_snippet}
              </pre>
            </div>
          ) : null}
        </>
      )}
    </SharePanelShell>
  )
}
